if (!customElements.get('wls-product-card')) {
  class WLSProductCard extends HTMLElement {
    constructor() {
      super();
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      this.productData = null;
      this.eventListenersAdded = false;
      this.classes = this.initializeClasses();
      this.selectors = this.initializeSelectors();
      this.cartData = JSON.parse(this.selectors.cartDataScript.textContent);
      this.observer = new MutationObserver(this.onCartDataChanged.bind(this));
      if (this.selectors.cartDataScript) {
        this.observer.observe(this.selectors.cartDataScript, { childList: true, subtree: true });
      }
      this.isRefillProduct = false;
    }

    initializeSelectors() {
      return {
        packSizeOptions: this.querySelector(`.${this.classes.packSizeOptions}`),
        cardFlavorsSlider: this.querySelector(`.${this.classes.cardFlavorsSlider}`),
        form: this.querySelector(`.${this.classes.from}`),
        variantInputs: this.querySelectorAll(`.${this.classes.variantInput}`),
        inputTypeName: this.querySelector(this.classes.inputTypeName),
        price: this.querySelector(`.${this.classes.price}`),
        compareAtPrice: this.querySelector(`.${this.classes.compareAtPrice}`),
        addToCartQuantityNumber: this.querySelector(`.${this.classes.addToCartQuantityNumber}`),
        btnQuantityPlus: this.querySelector(`.${this.classes.btnQuantityPlus}`),
        btnQuantityMinus: this.querySelector(`.${this.classes.btnQuantityMinus}`),
        btnQuantity: this.querySelector(`.${this.classes.btnQuantity}`),
        btnAddToCart: this.querySelector(`.${this.classes.btnAddToCart}`),
        productId: this.getAttribute('data-product-id'),
        currentVariantCard: this.getAttribute('data-current-variant-id'),
        btnAddToCartLoadingSpinner: this.querySelector(`.${this.classes.btnAddToCart} .${this.classes.loadingSpinner}`),
        btnQuantityLoadingSpinner: this.querySelector(`.${this.classes.btnQuantity} .${this.classes.loadingSpinner}`),
        cartDataScript: document.getElementById(`CartData`),
        productImage: this.querySelector(`.${this.classes.productImage}`),
        productCardLinks: this.querySelectorAll(`.${this.classes.productCardLink}`),
        productCardTitle: this.querySelector(`.${this.classes.productCardTitle}`),
      };
    }

    initializeClasses() {
      return {
        active: 'active',
        loading: 'loading',
        disabled: 'disabled',
        hidden: 'hidden',
        packSizeOptions: 'js-pack-size-options',
        cardFlavorsSlider: 'js-card-flavors-slider',
        from: 'js-wls-product-card-form',
        variantInput: 'js-wls-product-card-variant-input',
        inputTypeName: '[name="id"]',
        price: 'js-add-to-cart-btn-price',
        compareAtPrice: 'js-add-to-cart-btn-compare-price',
        currentVariantId: 'data-current-variant-id',
        loadingSpinner: 'loading__spinner',
        btnQuantity: 'js-wls-add-to-cart-quantity',
        btnAddToCart: 'js-wls-add-to-cart-btn',
        addToCartQuantityNumber: 'js-add-to-cart-quantity-number',
        attToCartQuantityText: 'js-add-to-cart-quantity-text',
        btnQuantityPlus: 'js-add-to-cart-quantity-plus',
        btnQuantityMinus: 'js-add-to-cart-quantity-minus',
        productImage: 'js-wls-product-card-image',
        productCardLink: 'js-product-card-link',
        productCardTitle: 'js-product-card-title',
      };
    }

    /**
     * Lifecycle method called when the element is added to the DOM.
     * It sets up event listeners for each tag to handle tag selection.
     *
     * @returns {void}
     */
    connectedCallback() {
      if (this.selectors.packSizeOptions) {
        this.initializeSwiper(this.selectors.packSizeOptions, {
          spaceBetween: 4,
          slidesPerView: 'auto',
          breakpoints: {
            768: {
              spaceBetween: 8,
            },
          },
        });
      }

      if (this.selectors.cardFlavorsSlider) {
        this.initializeSwiper(this.selectors.cardFlavorsSlider, {
          spaceBetween: 2,
          slidesPerView: 'auto',
          breakpoints: {
            768: {
              spaceBetween: 4,
            },
          },
        });
      }

      if (this.selectors.variantInputs) {
        this.selectors.variantInputs.forEach((input) => {
          input.addEventListener('change', this.bindOnVariantChange.bind(this));
        });
      }

      if (this.selectors.form) {
        this.selectors.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      if (this.selectors.btnAddToCart && this.selectors.btnQuantity) {
        this.initStateCard();
      }
    }

    /**
     * Handles the mutation observer for cart data changes.
     * @param {MutationRecord[]} mutationsList - The list of mutations.
     * @param {MutationObserver} observer - The observer instance.
     * @returns {void}
     * This function updates the cart data and product card state when the cart data changes.
     * It is triggered by the mutation observer.
     */
    onCartDataChanged(mutationsList, observer) {
      this.cartData = JSON.parse(this.selectors.cartDataScript.textContent);
      this.updateCartStateForVariant();
    }

    /**
     * Handles the change event for variant inputs.
     * @param {Event} e - The change event.
     * @returns {void}
     * This function updates the product card based on the selected variant.
     * It fetches the product data and updates the cart state.
     */
    bindOnVariantChange(e) {
      const currentVariantId = e.target.getAttribute('value');
      const productUrl = e.target.getAttribute('data-product-url');
      const productId = e.target.getAttribute('product-id');
      const isRefillProduct = e.target.hasAttribute('data-refill-product');
      this.isRefillProduct = isRefillProduct;

      this.fetchProductData(productUrl, currentVariantId, productId);
    }

    /**
     * Update the cart state based on the selected variant.
     *
     * @param {string} currentVariantId - The selected variant value.
     */
    updateCartStateForVariant() {
      const currentVariantId = Number(this.getAttribute('data-current-variant-id'));
      const itemInCart = this.cartData.items.find((item) => item.variant_id === currentVariantId);
      if (this.selectors.addToCartQuantityNumber) {
        if (itemInCart) {
          this.selectors.addToCartQuantityNumber.textContent = itemInCart.quantity;
          this.handleProductCardButton('itemInCart');
        } else {
          this.handleProductCardButton('itemNotInCart');
        }
      }
    }

    /**
     * Fetch product data from the server.
     *
     * @param {string} productUrl - The URL of the product.
     * @param {string} currentVariantId - The selected variant value.
     * @returns {void}
     */
    fetchProductData(productUrl, currentVariantId, productId) {
      const isProductInProductData = this.productData?.product.variants.some(
        (variant) => variant.product_id === Number(productId)
      );
      const url = `${productUrl}.json`;
      if (!this.productData || !isProductInProductData) {
        this.selectors.packSizeOptions?.classList.add(this.classes.loading);

        fetch(url)
          .then((response) => response.json())
          .then((data) => {
            this.productData = data;
            this.updateProductCard(this.productData, currentVariantId);
            this.selectors.packSizeOptions?.classList.remove(this.classes.loading);
          })
          .catch((error) => console.error('Error fetching product data:', error));
      } else {
        this.updateProductCard(this.productData, currentVariantId);
      }
    }

    /**
     * Initialize the state of the product card.
     * @returns {void}
     * This function sets up the initial state of the product card based on the cart data.
     * It adds event listeners to the plus and minus buttons for quantity adjustment.
     */
    initStateCard() {
      if (!this.eventListenersAdded) {
        this.handleProductCardButton('itemNotInCart');

        this.cartData.items.forEach((item) => {
          const variantId = item.variant_id;
          const variantInput = Number(this.getAttribute('data-current-variant-id'));

          if (variantInput === variantId) {
            this.selectors.addToCartQuantityNumber.textContent = item.quantity;
            this.handleProductCardButton('itemInCart');
          }
        });

        this.selectors.btnQuantityPlus?.addEventListener('click', this.plusButtonClick.bind(this));
        this.selectors.btnQuantityMinus?.addEventListener('click', this.minusButtonClick.bind(this));

        this.eventListenersAdded = true;
      }
    }

    /**
     * @returns {void}
     * Handles the click event on the plus button.
     * Increments the quantity of the product in the cart.
     */
    plusButtonClick() {
      const newQuantity = Number(this.selectors.addToCartQuantityNumber.textContent) + 1;
      const variantId = this.getAttribute(`data-current-variant-id`);
      this.selectors.addToCartQuantityNumber.textContent = newQuantity;
      this.updateCartItemQuantity(variantId, newQuantity);
    }

    /**
     * @returns {void}
     * Handles the click event on the minus button.
     * Increments the quantity of the product in the cart.
     * */
    minusButtonClick() {
      const newQuantity = Math.max(0, Number(this.selectors.addToCartQuantityNumber.textContent) - 1); // Obezbeđuje da količina ne padne ispod 0
      const variantId = this.getAttribute(`data-current-variant-id`);
      this.selectors.addToCartQuantityNumber.textContent = newQuantity;
      if (newQuantity >= 0) {
        this.updateCartItemQuantity(variantId, newQuantity);
      }
    }

    /**
     * Handle the state of the product card button.
     * @param {string} state - The state of the button ('itemInCart' or 'itemNotInCart').
     * @returns {void}
     * This function updates the button state based on whether the item exists in the cart or has been removed.
     */
    handleProductCardButton(state) {
      if (this.selectors.btnAddToCart.hasAttribute('data-out-of-stock')) return;

      if (state === 'itemInCart') {
        // Add to cart button
        if (this.selectors.btnAddToCart) {
          this.selectors.btnAddToCart.classList.add(this.classes.hidden);
          this.selectors.btnAddToCart.setAttribute('aria-disabled', true);
          this.selectors.btnAddToCart.setAttribute('disabled', true);
        }

        // Quantity button
        if (this.selectors.btnQuantity) {
          this.selectors.btnQuantity.classList.remove(this.classes.hidden);
          this.selectors.btnQuantity.removeAttribute('aria-disabled');
          this.selectors.btnQuantity.removeAttribute('disabled');
        }
      }

      if (state === 'itemNotInCart') {
        // Add to cart button
        if (this.selectors.btnAddToCart) {
          this.selectors.btnAddToCart.classList.remove(this.classes.hidden);
          this.selectors.btnAddToCart.removeAttribute('aria-disabled');
          this.selectors.btnAddToCart.removeAttribute('disabled');
        }
        // Quantity button
        if (this.selectors.btnQuantity) {
          this.selectors.btnQuantity.classList.add(this.classes.hidden);
          this.selectors.btnQuantity.setAttribute('aria-disabled', true);
          this.selectors.btnQuantity.setAttribute('disabled', true);
        }
      }
    }

    /**
     * Update the quantity of the item in the cart.
     *
     * @param {number} variantId - The variant ID.
     * @param {number} newQuantity - The new quantity.
     * @returns {void}
     */
    updateCartItemQuantity(variantId, newQuantity) {
      this.selectors.btnQuantity.classList.add(this.classes.loading);
      this.selectors.btnQuantity.setAttribute('disabled', true);
      this.selectors.btnQuantityPlus.setAttribute('disabled', true);
      this.selectors.btnQuantityMinus.setAttribute('disabled', true);

      const sections = [...this.cart.getSectionsToRender().map((section) => section.id)];
      this.selectors.btnQuantityLoadingSpinner.classList.remove(this.classes.hidden);

      const body = JSON.stringify({
        id: `${variantId}`,
        quantity: newQuantity,
        sections,
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.text())
        .then((updatedCart) => {
          const parsedUpdatedCart = JSON.parse(updatedCart);

          this.cartData = parsedUpdatedCart;

          this.updateCartDataScript();

          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(parsedUpdatedCart, false);
          });
          // Handlend by watching for the cart data changes script
          // this.updateAllProductCards(variantId, newQuantity);
        })
        .catch((error) => {
          console.error('Error updating cart:', error);
        })
        .finally(() => {
          this.selectors.btnQuantity.classList.remove(this.classes.loading);
          this.selectors.btnQuantity.removeAttribute('disabled');
          this.selectors.btnQuantityPlus.removeAttribute('disabled', true);
          this.selectors.btnQuantityMinus.removeAttribute('disabled', true);
          this.selectors.btnQuantityLoadingSpinner.classList.add(this.classes.hidden);

          if (newQuantity === 0) {
            this.handleProductCardButton('itemNotInCart');
          }
        });
    }

    /**
     * Update the product card with the selected variant data.
     *
     * @param {Object} data - The product data.
     * @param {string} currentVariantId - The selected variant value.
     * @returns {void}
     */
    updateProductCard(data, currentVariantId) {
      const variant = data.product.variants.find((variant) => {
        const optionKeys = Object.keys(variant).filter((key) => key.startsWith('option'));
        return optionKeys.some((key) => variant[key] === currentVariantId);
      });
      const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: variant.price_currency });
      const isVariantImageAvailable = data.product.images.some((img) => img.variant_ids.includes(variant.id));
      const image = data.product.images.find((img) => img.variant_ids.includes(variant.id));

      // Update the product card links with the new product handle
      this.selectors.inputTypeName.value = variant.id;
      this.setAttribute(this.classes.currentVariantId, variant.id);

      // Update the product card links with the new product handle
      this.selectors.productCardLinks.forEach((link) => {
        link.setAttribute('href', `/products/${data.product.handle}`);
      });

      this.selectors.productCardTitle.innerHTML = data.product.title;
      // Update the product card image
      if (this.isRefillProduct) {
        this.updatePorductCardImage(data.product.image);
      } else {
        if (isVariantImageAvailable) {
          if (image) {
            this.updatePorductCardImage(image);
          } else {
            this.updatePorductCardImage(data.product.image);
          }
        } else {
          if (!this.isRefillProduct) {
            this.updatePorductCardImage(data.product.image);
          }
        }
      }

      // Update prices
      if (this.selectors.price && variant.price) {
        this.selectors.price.innerHTML = formatter.format(variant.price);
      }

      if (this.selectors.compareAtPrice && variant.compare_at_price) {
        if (variant.compare_at_price > variant.price) {
          this.selectors.compareAtPrice.classList.remove('hidden');
          this.selectors.compareAtPrice.innerHTML = formatter.format(variant.compare_at_price);
        } else {
          this.selectors.compareAtPrice.classList.add('hidden');
          this.selectors.compareAtPrice.innerHTML = '';
        }
      }

      this.updateCartStateForVariant();
    }

    /**
     *
     * Update the product card image.
     * @param {Object} image - The image object containing the source URL.
     * @returns {void}
     * This function updates the product card image source and source set attributes.
     * It is used to change the image displayed on the product card.
     */
    updatePorductCardImage(image) {
      this.selectors.productImage.setAttribute('src', image.src);
      this.selectors.productImage.setAttribute('srcset', image.src);
    }

    /**
     * Update all product cards with the new quantity.
     *
     * @param {number} variantValue - The variant value to match.
     * @param {number} newQuantity - The new quantity to set.
     */
    updateAllProductCards(variantValue, newQuantity) {
      const productCards = document.querySelectorAll('wls-product-card');

      productCards.forEach((card) => {
        const currentVariantId = card.getAttribute('data-current-variant-id');

        if (currentVariantId === String(variantValue)) {
          const quantityElement = card.querySelector(`.${this.classes.addToCartQuantityNumber}`);
          if (quantityElement) {
            quantityElement.textContent = newQuantity;
          }

          card.handleProductCardButton(newQuantity > 0 ? 'itemInCart' : 'itemNotInCart');
        }
      });
    }
    /**
     * Handles the form submission event.
     * @param {Event} evt - The event object.
     * @returns {void}
     * This function prevents the default form submission behavior,
     * disables the button, and sends an AJAX request to add the item to the cart.
     * It also updates the cart data and handles the response.
     */
    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.selectors.btnAddToCart.getAttribute('aria-disabled') === 'true') return;

      this.selectors.btnAddToCart.setAttribute('aria-disabled', true);
      this.selectors.btnAddToCart.classList.add(this.classes.loading);
      this.selectors.btnAddToCartLoadingSpinner.classList.remove(this.classes.hidden);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.selectors.form);
      if (this.cart) {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          this.error = false;
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
          this.cartData.items = [...this.cartData.items, response];
          this.updateCartDataScript();
          // Handlend by watching for the cart data changes script
          // this.handleProductCardButton('itemInCart');
          // this.updateAllProductCards(response.id, response.quantity);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.selectors.btnAddToCart.classList.remove(this.classes.loading);
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) {
            this.selectors.btnAddToCart.removeAttribute('aria-disabled');
          }
          this.selectors.btnAddToCartLoadingSpinner.classList.add(this.classes.hidden);

          CartPerformance.measureFromEvent('add:user-action', evt);
        });
    }

    updateCartDataScript() {
      const cartDataScript = document.getElementById('CartData');

      if (cartDataScript) {
        cartDataScript.textContent = JSON.stringify(this.cartData);
      }
    }

    /**
     * Initialize Swiper for pack size options.
     */
    initializeSwiper(el, settings) {
      this.swiper = new Swiper(el, {
        ...settings,
        freeMode: true,
        loop: false,
        pagination: false,
        navigation: false,
        mousewheel: {
          enabled: true,
        },
        centerInsufficientSlides: true,
        breakpoints: {
          768: {
            centerInsufficientSlides: false,
          },
        },
      });
    }
  }

  window.customElements.define('wls-product-card', WLSProductCard);
}
